import React from 'react';
import { Player } from '../../types';

interface StatsEntry {
  goals: number;
  assists: number;
  saves: number;
}

interface PlayerStatsInlineEditorProps {
  players: Player[];
  substitutes?: Player[];
  localStats: Record<string, StatsEntry>;
  onChange: (playerId: string, field: 'goals' | 'assists' | 'saves', delta: number) => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}

const counterBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: 'none',
  background: '#33383B',
  color: '#fff',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  flexShrink: 0,
};

const Counter = ({
  value,
  onMinus,
  onPlus,
  label,
}: {
  value: number;
  onMinus: () => void;
  onPlus: () => void;
  label: string;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ fontSize: 12, color: '#9CA3AF', minWidth: 14 }}>{label}</span>
    <button style={counterBtnStyle} onClick={onMinus}>−</button>
    <span style={{ width: 20, textAlign: 'center', fontWeight: 700, color: '#fff', fontSize: 15 }}>
      {value}
    </span>
    <button style={counterBtnStyle} onClick={onPlus}>+</button>
  </div>
);

const PlayerRow = ({
  player,
  entry,
  onChange,
}: {
  player: Player;
  entry: StatsEntry;
  onChange: (field: 'goals' | 'assists' | 'saves', delta: number) => void;
}) => {
  const hasStats = entry.goals > 0 || entry.assists > 0 || entry.saves > 0;
  const isGoalkeeper = player.position === 'ARQ' || player.preferredPosition === 'ARQ';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 10,
      background: '#23272A',
      border: `1px solid ${hasStats ? '#00E676' : '#33383B'}`,
      flexWrap: 'wrap',
      transition: 'border-color 0.2s',
    }}>
      {/* Status dot */}
      <div style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: hasStats ? '#00E676' : '#4B5563',
        flexShrink: 0,
      }} />

      {/* Name */}
      <span style={{ flex: 1, color: '#fff', fontWeight: 600, fontSize: 14, minWidth: 80 }}>
        {player.name}
        {!player.userId && (
          <span style={{ marginLeft: 6, fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>sin cuenta</span>
        )}
      </span>

      {/* Counters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Counter
          label="⚽"
          value={entry.goals}
          onMinus={() => onChange('goals', -1)}
          onPlus={() => onChange('goals', 1)}
        />
        <Counter
          label="🅰️"
          value={entry.assists}
          onMinus={() => onChange('assists', -1)}
          onPlus={() => onChange('assists', 1)}
        />
        {isGoalkeeper && (
          <Counter
            label="🧤"
            value={entry.saves}
            onMinus={() => onChange('saves', -1)}
            onPlus={() => onChange('saves', 1)}
          />
        )}
      </div>
    </div>
  );
};

export const PlayerStatsInlineEditor: React.FC<PlayerStatsInlineEditorProps> = ({
  players,
  substitutes = [],
  localStats,
  onChange,
  onSave,
  onCancel,
  saving = false,
}) => {
  const allPlayers = [...players, ...substitutes].filter(Boolean);

  return (
    <div style={{
      background: '#1A1D1F',
      borderRadius: 16,
      border: '1px solid #33383B',
      padding: 20,
      marginTop: 16,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 16, fontWeight: 700 }}>
            📊 Estadísticas del partido
          </h3>
          <p style={{ margin: '4px 0 0', color: '#9CA3AF', fontSize: 12 }}>
            Tocá [+] para sumar goles y asistencias. Se guarda todo junto.
          </p>
        </div>
        <button
          onClick={onCancel}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9CA3AF',
            fontSize: 20,
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: 6,
          }}
          title="Cancelar"
        >
          ✕
        </button>
      </div>

      {/* Player list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {allPlayers.map(player => (
          <PlayerRow
            key={player.id}
            player={player}
            entry={localStats[player.id] ?? { goals: 0, assists: 0, saves: 0 }}
            onChange={(field, delta) => onChange(player.id, field, delta)}
          />
        ))}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '12px 0',
            background: '#33383B',
            border: 'none',
            borderRadius: 10,
            color: '#9CA3AF',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          style={{
            flex: 2,
            padding: '12px 0',
            background: saving ? '#33383B' : '#00E676',
            border: 'none',
            borderRadius: 10,
            color: saving ? '#9CA3AF' : '#111',
            fontWeight: 700,
            fontSize: 14,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Guardando...' : 'Guardar estadísticas'}
        </button>
      </div>
    </div>
  );
};
